import React, { useEffect, useState } from 'react';
import { supabase } from '../../services/supabaseClient';
import DirectoryCard from '../../components/DirectoryCard';

const Direktori = () => {
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // State untuk Filter Lokal
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProvince, setSelectedProvince] = useState('');
  const [availableProvinces, setAvailableProvinces] = useState([]);
  const [selectedBidang, setSelectedBidang] = useState('');
  const [availableBidang, setAvailableBidang] = useState([]);

  useEffect(() => {
    const fetchDirectory = async () => {
      try {
        setIsLoading(true);
        // Fetching sangat bersih karena logic diserahkan ke DB View
        const { data, error } = await supabase
          .from('direktori_publik_v2_view')
          .select('*')
          .order('pesantren_name', { ascending: true });

        if (error) throw error;

        const fetchedData = data || [];
        setMembers(fetchedData);
        setFilteredMembers(fetchedData);

        // Ekstrak daftar provinsi unik dari data yang ditarik secara dinamis
        const provinces = [...new Set(fetchedData.map(item => item.province_name))].sort();
        setAvailableProvinces(provinces);

        // Daftar bidang usaha unik dari semua bidang milik pesantren yang tampil
        const bidang = [...new Set(fetchedData.flatMap(item => item.business_type_names || []))].sort();
        setAvailableBidang(bidang);

      } catch (error) {
        console.error('Error fetching direktori:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDirectory();
  }, []);

  // Logika Filter Dinamis (Berjalan setiap kali input pencarian atau dropdown berubah)
  useEffect(() => {
    let result = members;

    // Filter by Search (Nama Pesantren)
    if (searchTerm) {
      const lowercasedTerm = searchTerm.toLowerCase();
      result = result.filter(member =>
        member.pesantren_name.toLowerCase().includes(lowercasedTerm) ||
        member.membership_number.toLowerCase().includes(lowercasedTerm)
      );
    }

    // Filter by Province
    if (selectedProvince) {
      result = result.filter(member => member.province_name === selectedProvince);
    }

    // Filter by Bidang Usaha (cocok bila salah satu bidang pesantren sesuai)
    if (selectedBidang) {
      result = result.filter(member => (member.business_type_names || []).includes(selectedBidang));
    }

    setFilteredMembers(result);
  }, [searchTerm, selectedProvince, selectedBidang, members]);

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-12">
          <h1 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Direktori Anggota HEBITREN
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-gray-500 mx-auto">
            Daftar resmi pondok pesantren yang telah tergabung dan terverifikasi sebagai anggota Himpunan Ekonomi Bisnis Pesantren.
          </p>
        </div>

        {/* Filter & Search Bar */}
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 mb-8">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search Input */}
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Cari nama pesantren atau nomor anggota..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-gray-50 placeholder-gray-500 focus:outline-none focus:bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm transition-colors"
              />
            </div>

            {/* Bidang Usaha Dropdown */}
            <div className="sm:w-64">
              <select
                value={selectedBidang}
                onChange={(e) => setSelectedBidang(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm rounded-md bg-gray-50 border transition-colors"
              >
                <option value="">Semua Bidang Usaha</option>
                {availableBidang.map((bidang) => (
                  <option key={bidang} value={bidang}>{bidang}</option>
                ))}
              </select>
            </div>

            {/* Province Dropdown */}
            <div className="sm:w-64">
              <select
                value={selectedProvince}
                onChange={(e) => setSelectedProvince(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm rounded-md bg-gray-50 border transition-colors"
              >
                <option value="">Semua Provinsi</option>
                {availableProvinces.map((prov, index) => (
                  <option key={index} value={prov}>{prov}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Content Grid */}
        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
          </div>
        ) : filteredMembers.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl border border-dashed border-gray-300">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Tidak ada data ditemukan</h3>
            <p className="mt-1 text-sm text-gray-500">Coba sesuaikan kata kunci pencarian atau filter Anda.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredMembers.map((member) => (
                <DirectoryCard key={member.id} member={member} />
              ))}
            </div>

            {/* Summary */}
            <div className="mt-8 text-center text-sm text-gray-500">
              Menampilkan {filteredMembers.length} anggota
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Direktori;